
import { useState, useEffect, useCallback } from "react";
import { Search, X } from "lucide-react";
import { useFaqs } from "@/hooks/useFaqs";
import { Button } from "@/components/ui/button";

interface FaqSearchProps {
  onSelectFaq: (faqId: string) => void;
}

export const FaqSearch = ({ onSelectFaq }: FaqSearchProps) => {
  const { faqs, searchQuery, searchFaqs, totalCount, refreshFaqs } = useFaqs();
  const [inputValue, setInputValue] = useState(searchQuery);
  
  // Debounce search input to improve performance
  useEffect(() => {
    const timer = setTimeout(() => {
      searchFaqs(inputValue);
    }, 300);
    
    return () => clearTimeout(timer);
  }, [inputValue, searchFaqs]);
  
  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    setInputValue("");
    searchFaqs("");
  };
  
  const handleSelectFaq = useCallback((faqId: string) => {
    onSelectFaq(faqId);
  }, [onSelectFaq]);
  
  return (
    <div className="relative w-full max-w-3xl mx-auto mb-8">
      <div className="relative flex items-center border rounded-lg shadow-sm">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
        <input
          className="w-full py-3 px-10 bg-background rounded-lg focus:outline-none focus:ring-2 focus:ring-corporate-primary-50"
          placeholder={`Search ${totalCount} FAQs...`}
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          aria-label="Search FAQs"
        />
        {inputValue && (
          <button 
            className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-muted-foreground hover:text-foreground"
            onClick={handleClear}
            aria-label="Clear search"
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>
      
      {/* Display search results dropdown */}
      {inputValue.trim() && faqs.length > 0 && (
        <div className="absolute top-full left-0 right-0 mt-1 bg-background rounded-lg border shadow-lg z-50 max-h-96 overflow-y-auto">
          <div className="p-2 border-b">
            <p className="text-sm text-muted-foreground">
              Found {faqs.length} {faqs.length === 1 ? 'result' : 'results'} for "{inputValue}"
            </p>
          </div>
          <ul>
            {faqs.map((faq: any) => (
              <li
                key={faq.id}
                onClick={() => handleSelectFaq(faq.id)}
                className="p-3 hover:bg-muted cursor-pointer border-b last:border-b-0"
              >
                <div className="font-medium">{faq.question}</div>
                <div className="text-xs text-muted-foreground mt-1 truncate">
                  {faq.answer.replace(/<[^>]*>/g, '').substring(0, 100)}...
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}
      
      {inputValue.trim() && faqs.length === 0 && (
        <div className="absolute top-full left-0 right-0 mt-1 bg-background rounded-lg border shadow-lg z-50">
          <div className="py-6 text-center">
            <p className="text-muted-foreground mb-2">No FAQs found matching your search.</p>
            <Button 
              variant="outline" 
              size="sm" 
              onClick={() => {
                setInputValue("");
                refreshFaqs();
              }}
              className="mt-2"
            >
              Show all FAQs
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};
